import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { http } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useCart } from "../context/CartContext";

export const CheckoutSuccess = () => {
  const [sp] = useSearchParams();
  const { clear } = useCart();
  const [state, setState] = useState({ phase: "checking" });
  const tries = useRef(0);
  useSeo({ title: "Order confirmation", noindex: true });

  useEffect(() => {
    const sid = sp.get("session_id");
    let timer;
    const poll = async () => {
      try {
        const r = (await http.get(`/payments/status/${sid}`)).data;
        if (r.payment_status === "paid") {
          clear();
          return setState({ phase: "paid", order: r.order_number });
        }
        if (["expired", "failed", "payment_failed"].includes(r.payment_status)) return setState({ phase: "failed" });
      } catch {
        return setState({ phase: "failed" });
      }
      tries.current += 1;
      if (tries.current < 15) timer = setTimeout(poll, 2000);
      else setState({ phase: "pending" });
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  return (
    <section className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
      {state.phase === "checking" && (
        <><Loader2 className="w-14 h-14 mx-auto text-turq animate-spin" /><h1 className="mt-6 text-3xl font-extrabold text-navy">Confirming your payment...</h1></>
      )}
      {state.phase === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto text-turq" />
          <p className="font-brush text-5xl text-tpink mt-4">You're all set!</p>
          <h1 className="mt-2 text-3xl font-extrabold text-navy" data-testid="payment-confirmed">Order {state.order} confirmed</h1>
          <p className="mt-4 text-navy/70">Your gear is now heading into production. We'll email you when it ships.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/track-order" className="rounded-full bg-navy text-white px-6 py-3 font-bold">Track order</Link>
            <Link to="/destinations" className="rounded-full bg-sun text-navy px-6 py-3 font-bold">Keep shopping</Link>
          </div>
        </>
      )}
      {state.phase === "pending" && (
        <><h1 className="text-3xl font-extrabold text-navy" data-testid="payment-pending">Payment still processing</h1><p className="mt-4 text-navy/70">We'll email you as soon as it's confirmed. You can refresh this page in a minute.</p></>
      )}
      {state.phase === "failed" && (
        <><XCircle className="w-16 h-16 mx-auto text-tpink" /><h1 className="mt-6 text-3xl font-extrabold text-navy" data-testid="payment-failed">We couldn't confirm that payment</h1><p className="mt-4 text-navy/70">You haven't been charged for an unconfirmed payment. Your cart is still saved.</p><Link to="/checkout" className="inline-block mt-8 rounded-full bg-sun px-6 py-3 font-bold text-navy">Try again</Link></>
      )}
    </section>
  );
};

export const CheckoutCancel = () => {
  useSeo({ title: "Checkout cancelled", noindex: true });
  return (
    <section className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="checkout-cancel-page">
      <p className="font-brush text-5xl text-turq">No worries!</p>
      <h1 className="mt-2 text-3xl font-extrabold text-navy">Checkout was cancelled</h1>
      <p className="mt-4 text-navy/70">You weren't charged. Your cart is saved whenever you're ready.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/cart" className="rounded-full bg-navy text-white px-6 py-3 font-bold" data-testid="cancel-back-to-cart">Back to cart</Link>
        <Link to="/checkout" className="rounded-full bg-sun text-navy px-6 py-3 font-bold">Try checkout again</Link>
      </div>
    </section>
  );
};
